import React from 'react';
import CustomTh from '../../../../../components/table/CustomTh';
import CustomTd from '../../../../../components/table/CustomTd';
import { useTranslation } from 'react-i18next';
import TableFunction from '../../../../../components/table/TableFunction';
import { useDispatch, useSelector } from 'react-redux';
import { DownloadProviderImage } from '../../../../../reduxToolkit/Providers/providersApi';
import DropDownMenuProfile from '../../../../../components/DropDown/DropDownMenuProfile';
import DropDownMenuItem from '../../../../../components/DropDown/DropDownMenuItem';
import { status, STATUS_MODEL } from "../../../../../enums/statusEnum";
import CustomChip from "../../../../../components/CustomChip";
import { statusColor } from "../../../../../enums/statusColorEnum";
import CustomImageTable from "../../../../../components/CustomImageTable";
import {EMPTY_VALUES, getImageByExt} from "../../../../../Apis/functions";
import DownloadIcon from "@mui/icons-material/Download";
import { Check, Clear } from "@mui/icons-material";
import CustomDescriptionLabel from "../../../../../components/CustomDescriptionLabel";
/**
 * @Email : kpozos@ibl.mx
 * @Author: Karen Yunuen Pozos Arias
 * @jobTittle: Full Stack Developer
 *
 */
// Component to render a document table with actions (approve, deny, download) based on permissions and status
const DocumentAppTable = ({
                              data,
                              setSelectedList,
                              docType,
                              userType,
                              employee,
                              handleApproveDocument,
                              handleDenyDocument,
                              tableId = "tbl_dat_table",
                              disabled
                          }) => {

    const { t } = useTranslation(); // For translations
    const dispatch = useDispatch(); // Redux dispatch
    const DEV = useSelector((state) => state?.CommonsSlice?.development); // Development mode flag

    // Define table headers dynamically
    const getThead = () => {
        let header = [
            <CustomTh tableId={tableId} left={0} className='first_head'>{t('name')}</CustomTh>
        ];

        header.push(<CustomTh tableId={tableId}>{t('file')}</CustomTh>);
        header.push(<CustomTh tableId={tableId}>{t('comment')}</CustomTh>);
        header.push(<CustomTh tableId={tableId}>{t('status')}</CustomTh>);
        return header;
    };

    // Define table body dynamically based on data
    const getTbody = () => {
        if (!data || data.length === 0) return { body: [], data: [] };

        const body = data.map((item) => (
            <>
                {/* Document Name */}
                <CustomTd tableId={tableId} left={50} tooltip={item?.companyDocument}>
                    <div className={"d-block"}>
                        <CustomDescriptionLabel trans={item?.companyDocument || EMPTY_VALUES.DEFAULT}/>
                        {item.document && <CustomDescriptionLabel trans={item.document} bold={"normal"}/>}
                    </div>
                </CustomTd>

                {/* Document File or Placeholder */}
                <CustomTd>
                    {item?.path ? (
                        <CustomImageTable
                            tooltip={item?.path}
                            image={getImageByExt(item?.path?.split('.').pop())}
                            placement={"top"}
                        >
                            <span className={"text-ellipsis ml-1 vertical-align-middle"}>
                                {item?.path}
                            </span>
                        </CustomImageTable>
                    ) : t('no_file')}
                </CustomTd>

                {/* Comment */}
                <CustomTd tooltip={item?.validationComment}>
                    <span className={"text-ellipsis"}>
                        {item?.validationComment || EMPTY_VALUES.DEFAULT}
                    </span>
                </CustomTd>

                {/* Status with conditional chip */}
                <CustomTd tableId={tableId}>
                    {(docType?.approve &&
                        item?.statusId === STATUS_MODEL.DOCUMENT_IN_VALIDATION &&
                        item?.departmentId !== null &&
                        (employee?.departmentId === null || employee?.departmentId !== item?.departmentId)) ? (
                        <CustomChip
                            background={statusColor["department_no_authorized_to_approve"]}
                            legend={t('department_no_authorized_to_approve')}
                        />
                    ) : (
                        <CustomChip
                            background={statusColor[item?.statusId]}
                            legend={t(status[item?.statusId]) || EMPTY_VALUES.DEFAULT}
                        />
                    )}
                </CustomTd>
            </>
        ));

        return { body, data };
    };

    // Define action buttons for each row (Approve, Deny, Download)
    const getActionsButtons = (item, index) => {
        const canApproveOrDeny =
            !disabled &&
            docType?.approve &&
            item?.statusId === STATUS_MODEL.DOCUMENT_IN_VALIDATION &&
            (item?.departmentId === null || employee?.departmentId === item?.departmentId);

        const canDownload =
            item?.path &&
            (item?.departmentId === null || employee?.departmentId === item?.departmentId);

        if (canApproveOrDeny || canDownload) {
            return (
                <DropDownMenuProfile
                    openMenu={openMenu}
                    handleMenuToggle={handleMenuToggle}
                    menuId={`menu-${item.id}`}
                >
                    {/* Approve and Deny buttons */}
                    {canApproveOrDeny && (
                        <>
                            <DropDownMenuItem
                                index={index}
                                handleClose={handleClose}
                                name={t('approve')}
                                click={() => handleApproveDocument(item)}
                                icon={<Check/>}
                            />
                            <DropDownMenuItem
                                index={index}
                                handleClose={handleClose}
                                name={t('deny')}
                                click={() => handleDenyDocument(item)}
                                icon={<Clear/>}
                            />
                        </>
                    )}

                    {/* Download button */}
                    {canDownload && (
                        <DropDownMenuItem
                            id={`btn_dat_download_${item.id}`}
                            index={index}
                            handleClose={handleClose}
                            name={t('download')}
                            icon={<DownloadIcon/>}
                            click={() => {
                                const data = {
                                    id: item.id,
                                    option: item?.option,
                                    type: item.path,
                                };
                                dispatch(DownloadProviderImage(data));
                            }}
                        />
                    )}
                </DropDownMenuProfile>
            );
        }

        // If no actions are available
        return "--";
    };

    // Use shared table logic (head, body, menu handlers)
    const {
        openMenu,
        handleMenuToggle,
        handleClose,
        thead,
        tbody
    } = TableFunction({
        setSelectedList: setSelectedList,
        data: { content: data },
        getTbody: getTbody,
        getThead: getThead
    });

    // Return component state and handlers
    return {
        t,
        DEV,
        thead,
        tbody,
        tableId,
        getActionsButtons
    };
};

export default DocumentAppTable;
